<template>
  <div class="master-detail-wrapper" ref="container">
    <!-- Mobile/Small Screen Alert -->
    <v-alert
      v-if="$vuetify.breakpoint.smAndDown"
      type="warning"
      border="left"
      colored-border
      icon="mdi-monitor-cellphone"
      prominent
      elevation="2"
      class="ma-4"
    >
      <div class="title">{{ mobileAlertTitle }}</div>
      <div class="mt-2">{{ mobileAlertText }}</div>
      <v-btn
        v-if="fallbackRoute"
        class="mt-3"
        color="primary"
        outlined
        :to="fallbackRoute"
      >
        <v-icon left>mdi-arrow-left</v-icon>
        {{ fallbackRouteText }}
      </v-btn>
    </v-alert>

    <!-- Split Pane Container - Hidden on small screens -->
    <div
      v-else
      class="split-pane-container"
      :class="{ 'is-resizing': isResizing }"
    >
      <!-- Master Pane (List) -->
      <div ref="masterPane" class="master-pane" :style="masterPaneStyle">
        <slot name="master"></slot>
      </div>

      <!-- Resizable Divider -->
      <div v-if="resizable" class="split-divider" @mousedown="startResize">
        <div class="split-divider-handle">
          <v-icon small color="grey">mdi-drag-vertical</v-icon>
        </div>
      </div>

      <!-- Detail Pane -->
      <div ref="detailPane" class="detail-pane" :style="detailPaneStyle">
        <template v-if="hasSelection">
          <slot name="detail"></slot>
        </template>
        <template v-else>
          <slot name="empty-state">
            <div class="empty-state">
              <v-icon size="80" color="grey lighten-1">
                {{ emptyStateIcon }}
              </v-icon>
              <div class="empty-state-text mt-4 text-h6 grey--text">
                {{ emptyStateTitle }}
              </div>
              <div
                class="empty-state-subtext mt-2 body-2 grey--text text--darken-1"
              >
                {{ emptyStateSubtitle }}
              </div>
            </div>
          </slot>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import containerBreakpoints from "@/mixins/containerBreakpoints.js";

export default {
  name: "MasterDetailWrapper",
  mixins: [containerBreakpoints],
  props: {
    // The currently selected item ID - synced with query params if syncRoute is true
    selectedId: {
      type: String,
      default: null,
    },
    // Query param key for syncing selection to URL
    queryParamKey: {
      type: String,
      default: "selectedId",
    },
    // Whether to sync selection state with route query params
    syncRoute: {
      type: Boolean,
      default: true,
    },
    // Debounce delay for selection changes (ms)
    selectionDebounce: {
      type: Number,
      default: 300,
    },
    // Initial split position (percentage for master pane width)
    initialSplitPosition: {
      type: Number,
      default: 25,
    },
    // Enable/disable resizing of split panes
    resizable: {
      type: Boolean,
      default: true,
    },
    // Minimum master pane width in pixels
    minMasterPaneWidth: {
      type: Number,
      default: 400,
    },
    // Maximum master pane width in pixels
    maxMasterPaneWidth: {
      type: Number,
      default: 600,
    },
    // Minimum pane width in pixels (for detail pane)
    minPaneWidth: {
      type: Number,
      default: 400,
    },
    // Mobile alert configuration
    mobileAlertTitle: {
      type: String,
      default: "Split-Ansicht nicht verfügbar",
    },
    mobileAlertText: {
      type: String,
      default:
        "Die Split-Ansicht ist auf kleinen Bildschirmen nicht verfügbar.",
    },
    // Fallback route for mobile users
    fallbackRoute: {
      type: [Object, String],
      default: null,
    },
    fallbackRouteText: {
      type: String,
      default: "Zur Standardansicht",
    },
    // Empty state configuration
    emptyStateIcon: {
      type: String,
      default: "mdi-cursor-default-click",
    },
    emptyStateTitle: {
      type: String,
      default: "Kein Eintrag ausgewählt",
    },
    emptyStateSubtitle: {
      type: String,
      default: "Eintrag auswählen, um Details anzuzeigen.",
    },
  },
  data() {
    return {
      splitPosition: this.initialSplitPosition,
      isResizing: false,
      selectionDebounceTimer: null,
      internalSelectedId: null,
    };
  },
  computed: {
    hasSelection() {
      return (
        this.internalSelectedId !== null &&
        this.internalSelectedId !== undefined
      );
    },
    masterPaneStyle() {
      return { width: `${this.splitPosition}%` };
    },
    detailPaneStyle() {
      return { width: `${100 - this.splitPosition}%` };
    },
  },
  watch: {
    // Watch external selectedId prop
    selectedId: {
      immediate: true,
      handler(newId) {
        if (newId !== this.internalSelectedId) {
          this.internalSelectedId = newId;
        }
      },
    },
    // Watch route query for changes (if syncRoute enabled)
    "$route.query": {
      immediate: true,
      handler(newQuery) {
        if (this.syncRoute && newQuery[this.queryParamKey] !== undefined) {
          const queryId = newQuery[this.queryParamKey] || null;
          if (queryId !== this.internalSelectedId) {
            this.internalSelectedId = queryId;
            this.$emit("update:selectedId", queryId);
            this.$emit("selection-change", queryId);
          }
        }
      },
    },
  },
  mounted() {
    this.initContainerBreakpoints(this.$refs.container);

    // Initialize from route query if syncRoute is enabled
    if (this.syncRoute && this.$route.query[this.queryParamKey]) {
      this.internalSelectedId = this.$route.query[this.queryParamKey];
      this.$emit("update:selectedId", this.internalSelectedId);
    }
  },
  beforeDestroy() {
    this.stopResize();
    if (this.selectionDebounceTimer) {
      clearTimeout(this.selectionDebounceTimer);
    }
  },
  methods: {
    /**
     * Handle selection from master list
     * Called by parent component when user selects an item
     */
    handleSelection(id) {
      // Clear any pending debounce
      if (this.selectionDebounceTimer) {
        clearTimeout(this.selectionDebounceTimer);
      }

      // Debounce the selection change
      this.selectionDebounceTimer = setTimeout(() => {
        this.internalSelectedId = id;
        this.$emit("update:selectedId", id);
        this.$emit("selection-change", id);

        // Update route query if syncRoute is enabled
        if (this.syncRoute) {
          this.updateRouteQuery(id);
        }
      }, this.selectionDebounce);
    },

    /**
     * Clear the current selection
     */
    clearSelection() {
      this.internalSelectedId = null;
      this.$emit("update:selectedId", null);
      this.$emit("selection-change", null);

      if (this.syncRoute) {
        this.updateRouteQuery(null);
      }
    },

    /**
     * Update the route query parameter
     */
    updateRouteQuery(id) {
      const query = { ...this.$route.query };

      if (id) {
        query[this.queryParamKey] = id;
      } else {
        delete query[this.queryParamKey];
      }

      // Only update if query has actually changed
      if (this.$route.query[this.queryParamKey] !== query[this.queryParamKey]) {
        this.$router
          .replace({
            path: this.$route.path,
            query: query,
          })
          .catch(() => {}); // Ignore NavigationDuplicated errors
      }
    },

    /**
     * Start resizing the split panes
     */
    startResize(e) {
      if (!this.resizable) return;
      this.isResizing = true;
      e.preventDefault();
      document.addEventListener("mousemove", this.handleResize);
      document.addEventListener("mouseup", this.stopResize);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    },

    /**
     * Handle resize mouse movement
     */
    handleResize(e) {
      if (!this.isResizing) return;

      const container = this.$refs.container?.querySelector(
        ".split-pane-container"
      );
      if (!container) return;

      const rect = container.getBoundingClientRect();
      let newPosition = ((e.clientX - rect.left) / rect.width) * 100;

      // Calculate minimum percentage based on master pane min width
      const minMasterPercentage = (this.minMasterPaneWidth / rect.width) * 100;

      // Calculate maximum percentage based on master pane max width
      const maxMasterPercentage = (this.maxMasterPaneWidth / rect.width) * 100;

      // Calculate maximum percentage based on detail pane minimum width
      // Detail pane is (100 - splitPosition), so max split = 100 - detailMin
      const detailMinWidth = (this.minPaneWidth / rect.width) * 100;
      const maxBasedOnDetailMin = 100 - detailMinWidth;

      // The effective maximum is the smaller of the two constraints
      const effectiveMaxPercentage = Math.min(
        maxMasterPercentage,
        maxBasedOnDetailMin
      );

      // Constrain between minimum and effective maximum
      newPosition = Math.max(
        minMasterPercentage,
        Math.min(effectiveMaxPercentage, newPosition)
      );

      this.splitPosition = newPosition;
    },

    /**
     * Stop resizing
     */
    stopResize() {
      this.isResizing = false;
      document.removeEventListener("mousemove", this.handleResize);
      document.removeEventListener("mouseup", this.stopResize);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    },
  },
};
</script>

<style scoped>
.master-detail-wrapper {
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
}

.split-pane-container {
  display: flex;
  flex-direction: row;
  height: calc(100vh - 64px); /* Account for app bar */
  width: 100%;
  overflow: hidden;
  position: relative;
}

.split-pane-container.is-resizing {
  user-select: none;
}

.master-pane {
  height: 100%;
  overflow: hidden;
  background: inherit;
  border-right: 1px solid rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: column;
}

.master-pane > * {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.detail-pane {
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  background: inherit;
  flex: 1;
}

.split-divider {
  width: 8px;
  background-color: #e0e0e0;
  cursor: col-resize;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  position: relative;
}

.split-divider:hover {
  background-color: #1976d2; /* Primary blue */
}

.split-divider-handle {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 12px;
  padding: 2px;
  height: 50px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 32px;
  text-align: center;
}

.empty-state-text {
  max-width: 280px;
}

.empty-state-subtext {
  max-width: 320px;
}

/* Dark mode support */
.theme--dark .master-pane {
  border-right-color: rgba(255, 255, 255, 0.12);
}

.theme--dark .split-divider {
  background-color: #424242;
}

.theme--dark .split-divider:hover {
  background-color: #2196f3; /* Lighter blue for dark theme */
}

.theme--dark .split-divider-handle {
  background-color: rgba(66, 66, 66, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.12);
}
</style>
